import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Modal,
  Box,
  TextField,
  IconButton,
  Autocomplete,
  Container,
  Chip,
  Card,
  CardContent,
  Fade,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  InputAdornment,
  Tooltip,
  Avatar,
  FormControl,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Logout,
  Dashboard,
  VpnKey,
  Close,
  CheckCircle,
  Error as ErrorIcon,
  ArrowBack,
  Search,
  Person,
  Extension,
  Code,
  CalendarToday,
} from '@mui/icons-material';
import { ref, set, get, child, update, remove } from 'firebase/database';
import { auth, database } from '../firebase/Config';
import { useNavigate } from 'react-router-dom';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  borderRadius: '16px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  p: 4,
};

// Função para gerar código de licença no formato XXXXX-XXXXX-XXXXX-XXXXX
const generateLicenseKey = () => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const segments = [];
  for (let i = 0; i < 4; i++) {
    let segment = '';
    for (let j = 0; j < 5; j++) {
      segment += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    segments.push(segment);
  }
  return segments.join('-');
};

const Licenses = () => {
  const [licenses, setLicenses] = useState([]);
  const [filteredLicenses, setFilteredLicenses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [plugins, setPlugins] = useState([]);
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [newLicense, setNewLicense] = useState({
    key: generateLicenseKey(),
    userEmail: '',
    pluginName: '',
    requestCode: '',
    date: new Date().toLocaleDateString(),
    createdBy: ''
  });
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const navigate = useNavigate();

  // Estados para validação e feedback
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    licenseId: null,
    licenseKey: '',
  });

  // Funções de feedback
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Validação de formulário
  const validateForm = () => {
    const newErrors = {};

    if (!newLicense.userEmail) {
      newErrors.userEmail = 'Usuário é obrigatório';
    }

    if (!newLicense.pluginName) {
      newErrors.pluginName = 'Plugin é obrigatório';
    }

    if (!newLicense.requestCode) {
      newErrors.requestCode = 'Código de requisição é obrigatório';
    }

    if (!isEditing) {
      // Verificar se já existe licença com esta chave
      const existingLicense = licenses.find(
        license => license.key === newLicense.key
      );
      if (existingLicense) {
        newErrors.key = 'Já existe uma licença com esta chave';
      }

      // Verificar se o usuário já tem licença para este plugin
      const duplicateLicense = licenses.find(
        license => license.userEmail === newLicense.userEmail && 
                   license.pluginName === newLicense.pluginName
      );
      if (duplicateLicense) {
        newErrors.pluginName = 'Este usuário já possui licença para este plugin';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpen = () => {
    setNewLicense({
      key: generateLicenseKey(),
      userEmail: '',
      pluginName: '',
      requestCode: '',
      date: new Date().toLocaleDateString(),
      createdBy: userEmail
    });
    setIsEditing(false);
    setErrors({});
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setErrors({});
  };

  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      setUserEmail(user.email);
    }
  }, []);

  const fetchLicenses = async () => {
    setLoading(true);
    const dbRef = ref(database);
    try {
      const snapshot = await get(child(dbRef, 'licenses'));
      if (snapshot.exists()) {
        const licensesData = snapshot.val();
        const licensesArray = Object.keys(licensesData).map((key) => ({
          id: key,
          ...licensesData[key],
        }));
        setLicenses(licensesArray);
        setFilteredLicenses(licensesArray);
      } else {
        setLicenses([]);
        setFilteredLicenses([]);
      }
    } catch (error) {
      console.error('Erro ao buscar licenças: ', error);
      showSnackbar('Erro ao carregar licenças', 'error');
    }
    setLoading(false);
  };

  const fetchUsers = async () => {
    const dbRef = ref(database);
    try {
      const snapshot = await get(child(dbRef, 'users'));
      if (snapshot.exists()) {
        const usersData = snapshot.val();
        const usersArray = Object.keys(usersData).map((key) => ({
          id: key,
          ...usersData[key],
        }));
        setUsers(usersArray);
      }
    } catch (error) {
      console.error('Erro ao buscar usuários: ', error);
    }
  };

  const fetchPlugins = async () => {
    const dbRef = ref(database);
    try {
      const snapshot = await get(child(dbRef, 'plugins'));
      if (snapshot.exists()) {
        const pluginsData = snapshot.val();
        const pluginsArray = Object.keys(pluginsData).map((key) => ({
          id: key,
          ...pluginsData[key],
        }));
        setPlugins(pluginsArray);
      }
    } catch (error) {
      console.error('Erro ao buscar plugins: ', error);
    }
  };

  const handleAddLicense = async () => {
    if (!validateForm()) {
      showSnackbar('Por favor, corrija os erros no formulário', 'error');
      return;
    }

    setSubmitLoading(true);
    try {
      const licenseId = newLicense.key;

      await set(ref(database, `licenses/${licenseId}`), {
        key: newLicense.key,
        userEmail: newLicense.userEmail,
        pluginName: newLicense.pluginName,
        requestCode: newLicense.requestCode,
        date: newLicense.date,
        createdBy: isEditing ? newLicense.createdBy : userEmail,
      });

      const user = users.find(u => u.email === newLicense.userEmail);

      if (user) {
        const userRef = ref(database, `users/${user.id}`);
        const userSnapshot = await get(userRef);

        if (userSnapshot.exists()) {
          const userData = userSnapshot.val();
          const updatedLicenses = userData.licenses ? [...userData.licenses] : [];

          updatedLicenses.push({
            licenseKey: newLicense.key,
            pluginName: newLicense.pluginName,
            requestCode: newLicense.requestCode,
          });

          await update(userRef, { licenses: updatedLicenses });
        }
      }

      showSnackbar('Licença cadastrada com sucesso!', 'success');
      fetchLicenses();
      handleClose();
    } catch (error) {
      console.error('Erro ao adicionar licença: ', error);
      showSnackbar('Erro ao cadastrar licença', 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (id) => {
    const license = licenses.find((lic) => lic.id === id);
    if (license) {
      setNewLicense(license);
      setIsEditing(true);
      setErrors({});
      setOpen(true);
    }
  };

  const handleOpenDeleteDialog = (license) => {
    setDeleteDialog({
      open: true,
      licenseId: license.id,
      licenseKey: license.key,
    });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, licenseId: null, licenseKey: '' });
  };

  const handleDelete = async () => {
    try {
      // Exclui a licença
      await remove(ref(database, `licenses/${deleteDialog.licenseId}`));

      // Exclui as renovações associadas à licença
      await remove(ref(database, `renewals/${deleteDialog.licenseId}`));

      showSnackbar('Licença excluída com sucesso!', 'success');
      fetchLicenses();
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Erro ao excluir licença: ', error);
      showSnackbar('Erro ao excluir licença', 'error');
    }
  };
  

  // Função de pesquisa
  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);

    if (value === '') {
      setFilteredLicenses(licenses);
    } else {
      const filtered = licenses.filter((license) => {
        const key = (license.key || '').toLowerCase();
        const userEmail = (license.userEmail || '').toLowerCase();
        const pluginName = (license.pluginName || '').toLowerCase();
        const requestCode = (license.requestCode || '').toLowerCase();

        return (
          key.includes(value) ||
          userEmail.includes(value) ||
          pluginName.includes(value) ||
          requestCode.includes(value)
        );
      });
      setFilteredLicenses(filtered);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilteredLicenses(licenses);
  };

  const handleLogout = async () => {
    await auth.signOut();
    navigate('/login');
  };

  useEffect(() => {
    fetchLicenses();
    fetchUsers();
    fetchPlugins();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      field: 'key',
      headerName: 'Chave de Licença',
      width: 220,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, width: '100%' }}>
          <VpnKey sx={{ color: '#48bb78', fontSize: 20 }} />
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'userEmail',
      headerName: 'Usuário',
      width: 200,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Chip
            avatar={<Avatar sx={{ bgcolor: '#3b82f6' }}>{params.value?.charAt(0).toUpperCase()}</Avatar>}
            label={params.value}
            size="small"
            sx={{ maxWidth: '100%' }}
          />
        </Box>
      ),
    },
    {
      field: 'pluginName',
      headerName: 'Plugin',
      width: 150,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'requestCode',
      headerName: 'Código de Requisição',
      width: 150,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'date',
      headerName: 'Data de Cadastro',
      width: 150,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'createdBy',
      headerName: 'Cadastrado por',
      width: 200,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 150,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', width: '100%' }}>
          <Tooltip title="Editar licença">
            <IconButton
              onClick={() => handleEdit(params.id)}
              sx={{
                color: '#f6ad55',
                '&:hover': { bgcolor: '#f6ad5520' }
              }}
            >
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir licença">
            <IconButton
              onClick={() => handleOpenDeleteDialog(params.row)}
              sx={{
                color: '#e53935',
                '&:hover': { bgcolor: '#e5393520' }
              }}
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      {/* Header */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)',
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <IconButton
            onClick={() => navigate('/home')}
            sx={{
              color: '#fff',
              mr: 2,
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            <ArrowBack />
          </IconButton>
          <Dashboard sx={{ mr: 2, fontSize: 32 }} />
          <Typography
            variant="h5"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              letterSpacing: '0.5px',
            }}
          >
            Gerenciamento de Licenças
          </Typography>
          {userEmail && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                avatar={<Avatar sx={{ bgcolor: '#fff', color: '#3b82f6' }}>{userEmail.charAt(0).toUpperCase()}</Avatar>}
                label={userEmail}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: '#fff',
                  fontWeight: 500,
                  px: 1,
                }}
              />
              <IconButton
                onClick={handleLogout}
                sx={{
                  color: '#fff',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <Logout />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ mt: 4, pb: 4 }}>
        <Fade in timeout={600}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              border: '1px solid rgba(255, 255, 255, 0.3)',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: '#2d3748',
                      mb: 1,
                    }}
                  >
                    <VpnKey sx={{ mr: 1, verticalAlign: 'middle', fontSize: 32, color: '#48bb78' }} />
                    Lista de Licenças
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#718096' }}>
                    Total de licenças cadastradas: <strong>{licenses.length}</strong>
                    {searchTerm && ` | Filtradas: ${filteredLicenses.length}`}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Add />}
                  onClick={handleOpen}
                  sx={{
                    background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                    color: '#fff',
                    fontWeight: 600,
                    py: 1.5,
                    px: 3,
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: '0 4px 15px rgba(72, 187, 120, 0.4)',
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(72, 187, 120, 0.6)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s',
                  }}
                >
                  Cadastrar Nova Licença
                </Button>
              </Box>

              {/* Barra de Pesquisa */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  placeholder="Pesquisar por chave, usuário, plugin ou código..."
                  value={searchTerm}
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: '#718096' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClearSearch}
                          size="small"
                          sx={{ color: '#718096' }}
                        >
                          <Close />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#f7fafc',
                      '&:hover': {
                        bgcolor: '#edf2f7',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#fff',
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ height: 500, width: '100%' }}>
                <DataGrid
                  rows={filteredLicenses}
                  columns={columns}
                  pageSize={10}
                  rowsPerPageOptions={[5, 10, 20, 50]}
                  checkboxSelection
                  disableSelectionOnClick
                  loading={loading}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': {
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    },
                    '& .MuiDataGrid-cell:focus': {
                      outline: 'none',
                    },
                    '& .MuiDataGrid-row:hover': {
                      bgcolor: '#f7fafc',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      bgcolor: '#f7fafc',
                      fontSize: '0.95rem',
                      fontWeight: 600,
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Fade>

        {/* Modal de Cadastro/Edição */}
        <Modal open={open} onClose={handleClose}>
          <Fade in={open}>
            <Box sx={modalStyle}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#2d3748',
                  }}
                >
                  {isEditing ? 'Editar Licença' : 'Cadastrar Nova Licença'}
                </Typography>
                <IconButton onClick={handleClose} sx={{ color: '#718096' }}>
                  <Close />
                </IconButton>
              </Box>

              <TextField
                label="Chave de Licença"
                fullWidth
                margin="normal"
                value={newLicense.key}
                disabled
                error={!!errors.key}
                helperText={errors.key || 'Gerada automaticamente'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VpnKey sx={{ color: '#48bb78' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontFamily: 'monospace',
                    fontWeight: 600,
                  },
                }}
              />

              <Autocomplete
                options={users}
                getOptionLabel={(option) => option.email}
                value={users.find(u => u.email === newLicense.userEmail) || null}
                onChange={(event, newValue) => {
                  setNewLicense({ ...newLicense, userEmail: newValue?.email || '' });
                  if (errors.userEmail) setErrors({ ...errors, userEmail: null });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Usuário"
                    fullWidth
                    margin="normal"
                    required
                    error={!!errors.userEmail}
                    helperText={errors.userEmail}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <Person sx={{ color: '#718096' }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <FormControl fullWidth margin="normal" error={!!errors.pluginName}>
                <TextField
                  select
                  label="Plugin"
                  value={newLicense.pluginName}
                  required
                  onChange={(e) => {
                    const selectedPlugin = plugins.find(plugin => plugin.name === e.target.value);
                    setNewLicense({
                      ...newLicense,
                      pluginName: selectedPlugin?.name || '',
                      requestCode: selectedPlugin?.requestCode || ''
                    });
                    if (errors.pluginName) setErrors({ ...errors, pluginName: null });
                  }}
                  error={!!errors.pluginName}
                  helperText={errors.pluginName}
                  SelectProps={{
                    native: true,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Extension sx={{ color: '#718096' }} />
                      </InputAdornment>
                    ),
                  }}
                >
                  <option value="">Selecione um plugin</option>
                  {plugins.map((plugin) => (
                    <option key={plugin.id} value={plugin.name}>
                      {plugin.name}
                    </option>
                  ))}
                </TextField>
              </FormControl>

              <TextField
                label="Código de Requisição"
                fullWidth
                margin="normal"
                value={newLicense.requestCode}
                disabled
                helperText="Preenchido automaticamente ao selecionar o plugin"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Code sx={{ color: '#718096' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Data de Cadastro"
                fullWidth
                margin="normal"
                value={newLicense.date}
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday sx={{ color: '#718096' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Cadastrado por"
                fullWidth
                margin="normal"
                value={isEditing ? newLicense.createdBy : userEmail}
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: '#718096' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleClose}
                  sx={{
                    py: 1.5,
                    borderColor: '#718096',
                    color: '#718096',
                    '&:hover': {
                      borderColor: '#2d3748',
                      bgcolor: '#f7fafc',
                    },
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleAddLicense}
                  disabled={submitLoading}
                  sx={{
                    py: 1.5,
                    background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                    color: '#fff',
                    fontWeight: 600,
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(72, 187, 120, 0.4)',
                    },
                    '&:disabled': {
                      background: '#cbd5e0',
                    },
                  }}
                >
                  {submitLoading ? (
                    <CircularProgress size={24} sx={{ color: '#fff' }} />
                  ) : (
                    <>
                      {isEditing ? (
                        <>
                          <CheckCircle sx={{ mr: 1 }} />
                          Salvar Alterações
                        </>
                      ) : (
                        <>
                          <Add sx={{ mr: 1 }} />
                          Adicionar Licença
                        </>
                      )}
                    </>
                  )}
                </Button>
              </Box>
            </Box>
          </Fade>
        </Modal>

        {/* Dialog de Confirmação de Exclusão */}
        <Dialog
          open={deleteDialog.open}
          onClose={handleCloseDeleteDialog}
          PaperProps={{
            sx: { borderRadius: 3 }
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon sx={{ color: '#e53935' }} />
            Confirmar Exclusão
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja excluir a licença <strong>{deleteDialog.licenseKey}</strong>?
              <br /><br />
              <strong>Atenção:</strong> Todas as renovações associadas a esta licença também serão excluídas.
              <br /><br />
              Esta ação não pode ser desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={handleCloseDeleteDialog}
              sx={{ color: '#718096' }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDelete}
              variant="contained"
              sx={{
                bgcolor: '#e53935',
                '&:hover': { bgcolor: '#c62828' }
              }}
              startIcon={<Delete />}
            >
              Excluir
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar para Feedback */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Licenses;
